import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getSpot, deleteSpot, uploadImage, imgSrc, requirePassword } from "../api";
import SpotFormModal from "../components/SpotFormModal";

export default function SpotDetail() {
  const { spotID } = useParams();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setStatus("loading");
    return getSpot(spotID)
      .then((data) => {
        setSpot(data);
        setActive(0);
        setStatus("ok");
      })
      .catch((err) => {
        setError(err.message);
        setStatus("error");
      });
  }, [spotID]);

  useEffect(() => {
    load();
  }, [load]);

  const onEdit = () => {
    if (requirePassword("edit this spot")) setEditing(true);
  };

  // Anyone can add a photo — no password required.
  const onAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    setUploading(true);
    try {
      await uploadImage(spot.spotID, file);
      await load();
    } catch (err) {
      alert("Photo upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!requirePassword(`delete "${spot.name}"`)) return;
    try {
      await deleteSpot(spot.spotID);
      navigate("/");
    } catch (e) {
      alert("Delete failed: " + e.message);
    }
  };

  if (status === "loading")
    return (
      <div className="state">
        <div className="spinner" />
        Loading…
      </div>
    );
  if (status === "error")
    return <div className="state error">Could not load spot: {error}</div>;

  const images = spot.images || [];
  const hero = images[active]?.imgURL;
  const tags = String(spot.tags || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <div className="detail">
      <div className="detail-top">
        <Link to="/" className="back-link">
          ← Back to map
        </Link>
        <div className="detail-actions">
          <label className="btn-primary add-photo">
            {uploading ? "Uploading…" : "📷 Add photo"}
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={uploading}
              onChange={onAddPhoto}
            />
          </label>
          <button className="btn-ghost" onClick={onEdit}>
            ✎ Edit
          </button>
        </div>
      </div>

      {hero ? (
        <img className="detail-hero" src={imgSrc(hero)} alt={spot.name} />
      ) : (
        <div className="detail-hero placeholder">
          {spot.name?.[0]?.toUpperCase() || "🛹"}
        </div>
      )}

      {images.length > 1 && (
        <div className="thumb-strip">
          {images.map((img, i) => (
            <img
              key={img.imgID}
              src={imgSrc(img.imgURL)}
              alt={spot.name}
              className={i === active ? "active" : ""}
              onClick={() => setActive(i)}
            />
          ))}
        </div>
      )}

      <h1>{spot.name}</h1>

      {tags.length > 0 && (
        <div className="tags">
          {tags.map((t) => (
            <span className="tag" key={t}>
              {t}
            </span>
          ))}
        </div>
      )}

      {spot.description && <p className="desc">{spot.description}</p>}

      <div className="detail-meta">
        {spot.lat != null && spot.lng != null && (
          <span>
            <span className="k">Coordinates</span> ·{" "}
            {Number(spot.lat).toFixed(4)}, {Number(spot.lng).toFixed(4)}
          </span>
        )}
        <span>
          <span className="k">Photos</span> · {images.length}
        </span>
        {spot.lat != null && spot.lng != null && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${spot.lat},${spot.lng}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--accent-2)" }}
          >
            Open in Google Maps ↗
          </a>
        )}
      </div>

      <div className="danger-zone">
        <button className="btn-delete" onClick={onDelete}>
          🗑 Delete this spot
        </button>
      </div>

      {editing && (
        <SpotFormModal
          spot={spot}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
          onPhotoChange={load}
        />
      )}
    </div>
  );
}
